import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosMedical } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import { resolveMedicalScope } from "./medicalScope";

/* ========= Helpers ========= */
/** Map UI payload -> DTO for CREATE (POST) */
const mapCreateBody = (values = {}) => {
  const t = String(values.type || "").toUpperCase();

  if (t === "ALLERGY") {
    // Supporte UI fields: name/severity ou item/reaction
    const item = values.item ?? values.name ?? "";
    const reaction = values.reaction ?? values.severity ?? null;
    if (!item) throw new Error("Allergy `item/name` is required");
    return { type: "ALLERGY", item, reaction };
  }

  if (t === "SURGERY") {
    // Supporte UI fields: item/type/label + year/date
    const item = values.item ?? values.type ?? values.label ?? "Surgery";
    const year = values.year ?? values.date; // year number ou 'YYYY-01-01'
    let date = undefined;
    if (typeof year === "number" && Number.isFinite(year)) {
      date = `${year}-01-01`;
    } else if (typeof year === "string" && year.trim()) {
      date = /^\d{4}-\d{2}-\d{2}$/.test(year) ? year : `${year}-01-01`;
    }
    return { type: "SURGERY", item, date };
  }

  throw new Error("`values.type` is required (ALLERGY or SURGERY).");
};

/** Map UI payload -> DTO for UPDATE (PUT) (partial fields, no forced type) */
const mapUpdateBody = (values = {}) => {
  const t = String(values.type || "").toUpperCase(); // facultatif en update
  const body = {};

  // Allergie
  if (t === "ALLERGY" || ("reaction" in values) || ("severity" in values)) {
    if (values.item ?? values.name) body.item = values.item ?? values.name;
    if (values.reaction ?? values.severity) body.reaction = values.reaction ?? values.severity;
    return body;
  }

  // Chirurgie
  if (t === "SURGERY" || ("year" in values) || ("date" in values) || ("label" in values) || ("type" in values)) {
    if (values.item ?? values.type ?? values.label) {
      body.item = values.item ?? values.type ?? values.label;
    }
    if (values.year !== undefined || values.date !== undefined) {
      const y = values.year ?? values.date;
      if (typeof y === "number" && Number.isFinite(y)) {
        body.date = `${y}-01-01`;
      } else if (typeof y === "string" && y.trim()) {
        body.date = /^\d{4}-\d{2}-\d{2}$/.test(y) ? y : `${y}-01-01`;
      }
    }
    return body;
  }

  return { ...values };
};

/* ========= THUNKS ========= */

export const fetchSurgicalHistory = createAsyncThunk(
  "surgicalHistory/fetch",
  async (target, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        target
      );
      const { data } = await axiosMedical.get(
        ENDPOINTS.MEDICAL.SURGICAL_HISTORY(scopedUserId),
        { headers }
      );
      if (!Array.isArray(data)) {
        return rejectWithValue("Format inattendu (array attendu).");
      }
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur réseau"
      );
    }
  }
);

export const addSurgicalHistory = createAsyncThunk(
  "surgicalHistory/add",
  async ({ userId, values }, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        userId
      );
      const body = mapCreateBody(values);
      const { data } = await axiosMedical.post(
        ENDPOINTS.MEDICAL.SURGICAL_HISTORY(scopedUserId),
        body,
        { headers: { "Content-Type": "application/json", ...headers } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur réseau"
      );
    }
  }
);

export const updateSurgicalHistory = createAsyncThunk(
  "surgicalHistory/update",
  async ({ userId, id, values }, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        userId
      );
      const body = mapUpdateBody(values);
      const { data } = await axiosMedical.put(
        ENDPOINTS.MEDICAL.UPDATE_SURGICAL_HISTORY(scopedUserId, id),
        body,
        { headers: { "Content-Type": "application/json", ...headers } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur mise à jour"
      );
    }
  }
);

export const removeSurgicalHistory = createAsyncThunk(
  "surgicalHistory/remove",
  async ({ userId, id }, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = resolveMedicalScope(
        getState(),
        userId
      );
      await axiosMedical.delete(ENDPOINTS.MEDICAL.DELETE_SURGICAL_HISTORY(scopedUserId, id), {
        headers,
      });
      return id;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur suppression"
      );
    }
  }
);
